import { useRef, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';

import { toast } from 'sonner';

import { HOST } from '@muzly/shared';

import { ShareConfirmDialog } from './ShareConfirmDialog';
import { ShareIcon } from '../../../../components';
import { loadDiagnosis, overallScore, subscribeDiagnoses } from '../../record';
import { shareLinkOf, shareResult, shareTextOf } from '../../share';

/**
 * 결과 상세 헤더의 공유 버튼(`ResultHeaderActions` 가 `SHARE_ENABLED` 일 때만 둔다).
 *
 * **버튼 유무와 링크는 저장된 레코드에서 나온다**(저장이 날 때마다 다시 읽는다).
 * 화면이 들고 있는 사본에는 `analyId` 가 없을 수 있다 — 회차 마감 뒤 레코드가
 * 한 번 더 저장되기 때문이다. 그래서 누르는 시점에 `loadDiagnosis` 로 다시 읽는다. `analyId` 가
 * 없으면 버튼이 없다(공유할 서버 회차가 없다).
 */
export const ShareAction = ({ recordId }: { recordId: string }) => {
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState(false);
  const [sharing, setSharing] = useState(false);
  const busy = useRef(false);

  // `useDiagnoses` 의 캐시는 인덱스(메타데이터)가 바뀔 때만 갱신된다 — `analyId` 만 더해진
  // 재저장은 못 본다. 그래서 저장소의 그 값 하나(문자열)를 직접 구독한다.
  const analyId = useSyncExternalStore(
    subscribeDiagnoses,
    () => loadDiagnosis(recordId)?.analyId ?? null,
  );
  if (!analyId) {
    return null;
  }

  const share = async () => {
    setConfirming(false);
    const record = loadDiagnosis(recordId);
    if (!record?.analyId || busy.current) {
      return;
    }
    busy.current = true;
    setSharing(true);
    try {
      const score = overallScore(record);
      const line =
        score === null
          ? t('web.result.share.textNoScore')
          : t('web.result.share.text', { score: Math.round(score) });
      const outcome = await shareResult(
        shareTextOf(line, shareLinkOf(record.analyId, HOST)),
      );
      if (outcome === 'copied') {
        toast(t('web.result.share.copied'));
      } else if (outcome === 'failed') {
        toast(t('web.result.share.failed'));
      }
    } finally {
      busy.current = false;
      setSharing(false);
    }
  };

  return (
    <>
      <button
        type="button"
        aria-label={t('web.result.share.label')}
        disabled={sharing}
        onClick={() => setConfirming(true)}
        className="flex h-11 w-11 items-center justify-center text-[22px] text-bk-900 disabled:text-bk-400"
      >
        <ShareIcon aria-hidden="true" />
      </button>
      {confirming && (
        <ShareConfirmDialog
          onConfirm={() => void share()}
          onCancel={() => setConfirming(false)}
        />
      )}
    </>
  );
};
